/**
 * Open.Video Commands & Best Practices
 *
 * The guide document is unchanged; only its wrapper is. It used to carry
 * its own 49px strip with a back link and no footer, so a visitor who landed
 * here from an email had no wordmark, no account control and no way into the
 * rest of the site. GuideShell supplies all three, and GuideFrame lets the
 * document size itself so the footer sits after it rather than behind a
 * second scrollbar.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_CONTENT = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Open.Video Reference Card | Caren Glasser</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --cream: oklch(0.98 0.008 85); --linen: oklch(0.93 0.01 85); --espresso: oklch(0.22 0.015 65); --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85); --white: oklch(1 0.004 85); --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22); --muted: oklch(0.52 0.015 65); --body: oklch(0.52 0.015 65);
  }
  body { background: var(--cream); color: var(--espresso); font-family: 'Plus Jakarta Sans', system-ui, sans-serif; min-height: 100vh; padding: 40px 20px 60px; }
  .container { max-width: 680px; margin: 0 auto; }
  .header { text-align: center; margin-bottom: 40px; padding-bottom: 36px; border-bottom: 1px solid var(--border); }
  .header .eyebrow { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--terracotta); margin-bottom: 14px; }
  .header h1 { font-family: 'Fraunces', Georgia, serif; font-size: clamp(26px, 5vw, 36px); font-weight: 700; color: var(--espresso); line-height: 1.2; margin-bottom: 14px; }
  .header p { font-size: 16px; color: var(--muted); line-height: 1.6; max-width: 460px; margin: 0 auto; }
  .tab-nav { display: flex; gap: 0; border-bottom: 2px solid var(--border); margin-bottom: 32px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .tab-btn { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); background: none; border: none; border-bottom: 2px solid transparent; margin-bottom: -2px; padding: 12px 20px; cursor: pointer; transition: all 0.2s ease; white-space: nowrap; }
  .tab-btn:hover { color: var(--espresso); }
  .tab-btn.active { color: var(--terracotta); border-bottom-color: var(--terracotta); }
  .tab-panel { display: none; }
  .tab-panel.active { display: block; }
  .section { margin-bottom: 28px; }
  .section-title { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: var(--terracotta); margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid var(--border); }
  .command-list { list-style: none; }
  .command-item { display: flex; align-items: flex-start; gap: 16px; padding: 12px 16px; border-bottom: 1px solid var(--border); transition: background 0.15s ease; }
  .command-item:last-child { border-bottom: none; }
  .command-item:hover { background: var(--terracotta-light); }
  .command-label { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; font-weight: 600; color: var(--espresso); min-width: 160px; flex-shrink: 0; padding-top: 1px; }
  .command-desc { font-size: 14px; color: var(--body); line-height: 1.5; }
  .command-desc em { font-style: italic; color: var(--muted); font-size: 14px; }
  .formula-box { background: var(--linen); border-left: 3px solid var(--terracotta); border-radius: 0 3px 3px 0; padding: 16px 20px; margin: 4px 0 16px; }
  .formula-box .label { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--terracotta); margin-bottom: 7px; }
  .formula-box p { font-size: 14px; color: var(--espresso); line-height: 1.6; margin: 0 0 6px; font-style: italic; }
  .formula-box .example { font-size: 14px; color: var(--muted); font-style: normal; margin: 0; }
  .practice-card { background: var(--white); border: 1px solid var(--border); border-radius: 3px; margin-bottom: 12px; overflow: hidden; }
  .practice-header { display: flex; align-items: center; gap: 14px; padding: 16px 20px; cursor: pointer; user-select: none; transition: background 0.15s ease; }
  .practice-header:hover { background: var(--terracotta-light); }
  .practice-icon { font-size: 18px; flex-shrink: 0; }
  .practice-title { font-family: 'Fraunces', Georgia, serif; font-size: 18px; font-weight: 600; color: var(--espresso); flex: 1; }
  .practice-toggle { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 18px; color: var(--border); flex-shrink: 0; transition: transform 0.25s ease, color 0.25s ease; line-height: 1; }
  .practice-card.open .practice-toggle { transform: rotate(45deg); color: var(--terracotta); }
  .practice-body { display: none; padding: 4px 20px 18px 52px; }
  .practice-card.open .practice-body { display: block; }
  .practice-body ul { list-style: none; }
  .practice-body ul li { font-size: 14px; line-height: 1.6; color: var(--body); padding: 5px 0 5px 18px; position: relative; border-bottom: 1px solid var(--border); }
  .practice-body ul li:last-child { border-bottom: none; }
  .practice-body ul li::before { content: "—"; position: absolute; left: 0; color: var(--terracotta); font-weight: 300; }
  .flywheel { background: var(--linen); border: 1px solid var(--border); border-radius: 3px; padding: 20px 24px; margin-top: 8px; }
  .flywheel .label { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase; color: var(--terracotta); margin-bottom: 12px; }
  .flywheel-steps { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .flywheel-step { background: var(--white); border: 1px solid var(--border); border-radius: 2px; padding: 8px 14px; font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; font-weight: 600; color: var(--espresso); }
  .flywheel-arrow { color: var(--terracotta); font-size: 14px; font-weight: 300; }
  .footer { text-align: center; margin-top: 48px; padding-top: 28px; border-top: 1px solid var(--border); }
  .footer p { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; font-size: 12px; color: oklch(0.88 0.012 85); letter-spacing: 0.04em; }
  .footer a { color: var(--terracotta); text-decoration: none; }
</style>
</head>
<body>
<div class="container">
  <div class="header">
    <p class="eyebrow">Resource Center: Quick Reference</p>
    <h1>Open.Video<br>Commands &amp; Best Practices</h1>
    <p>Everything you need in one place. Switch between tabs to find commands, formulas, and best practices by category.</p>
  </div>
  <div class="tab-nav">
    <button class="tab-btn active" onclick="switchTab('commands', this)">Commands</button>
    <button class="tab-btn" onclick="switchTab('seo', this)">SEO</button>
    <button class="tab-btn" onclick="switchTab('engagement', this)">Engagement</button>
    <button class="tab-btn" onclick="switchTab('distribution', this)">Distribution</button>
    <button class="tab-btn" onclick="switchTab('growth', this)">Growth</button>
    <button class="tab-btn" onclick="switchTab('youtube', this)">YouTube Import</button>
  </div>

  <!-- TAB: COMMANDS -->
  <div class="tab-panel active" id="tab-commands">
    <div class="section">
      <div class="section-title">Channel Setup</div>
      <ul class="command-list">
        <li class="command-item"><span class="command-label">Create Channel</span><span class="command-desc">Dashboard → "New Channel" → Name + Brand Colors</span></li>
        <li class="command-item"><span class="command-label">Branding</span><span class="command-desc">Upload logo (PNG), choose theme colors, adjust banner</span></li>
        <li class="command-item"><span class="command-label">Featured Video</span><span class="command-desc">Mark one video as "featured" to showcase at the top of your channel</span></li>
      </ul>
    </div>
    <div class="section">
      <div class="section-title">Video Upload</div>
      <ul class="command-list">
        <li class="command-item"><span class="command-label">Drag &amp; Drop</span><span class="command-desc">Drop MP4 or MOV file directly into the dashboard</span></li>
        <li class="command-item"><span class="command-label">Batch Upload</span><span class="command-desc">Select multiple files → system queues automatically</span></li>
        <li class="command-item"><span class="command-label">Title</span><span class="command-desc">Use your primary keyword at the start</span></li>
        <li class="command-item"><span class="command-label">Description</span><span class="command-desc">First 2 sentences must include your keyword + a CTA</span></li>
        <li class="command-item"><span class="command-label">Tags</span><span class="command-desc">Add 5–10 keyword tags per video: think search terms your audience uses</span></li>
        <li class="command-item"><span class="command-label">Thumbnail</span><span class="command-desc">Upload a custom 1280×720 image (text overlay recommended)</span></li>
      </ul>
    </div>
    <div class="section">
      <div class="section-title">Playlists</div>
      <ul class="command-list">
        <li class="command-item"><span class="command-label">Create Playlist</span><span class="command-desc">Dashboard → Playlists → "New Playlist" → Add videos</span></li>
        <li class="command-item"><span class="command-label">Reorder</span><span class="command-desc">Drag videos within a playlist to set the viewing sequence</span></li>
        <li class="command-item"><span class="command-label">Embed Playlist</span><span class="command-desc">Copy embed code from playlist settings → paste into any webpage</span></li>
      </ul>
    </div>
    <div class="section">
      <div class="section-title">Privacy &amp; Sharing</div>
      <ul class="command-list">
        <li class="command-item"><span class="command-label">Public</span><span class="command-desc">Visible to anyone with the link, and indexed by search engines</span></li>
        <li class="command-item"><span class="command-label">Unlisted</span><span class="command-desc">Accessible via direct link only, not indexed</span></li>
        <li class="command-item"><span class="command-label">Private</span><span class="command-desc">Only visible to you: use for drafts or internal content</span></li>
        <li class="command-item"><span class="command-label">Password</span><span class="command-desc">Set a password for gated access: great for paid content</span></li>
      </ul>
    </div>
  </div>

  <!-- TAB: SEO -->
  <div class="tab-panel" id="tab-seo">
    <div class="section">
      <div class="section-title">Title Formula</div>
      <div class="formula-box">
        <div class="label">The Open.Video Title Formula</div>
        <p>[Primary Keyword] + [Benefit or Outcome] + [Optional: Who It's For]</p>
        <div class="example">Example: "Video Hosting for Coaches: Own Your Content Without YouTube"</div>
      </div>
    </div>
    <div class="section">
      <div class="section-title">Description Formula</div>
      <div class="formula-box">
        <div class="label">The Open.Video Description Formula</div>
        <p>Sentence 1: State what the video is about + primary keyword.<br>Sentence 2: Who it's for + the main benefit.<br>Sentence 3+: Supporting details, timestamps, links.</p>
        <div class="example">End with: "Watch more on my channel → [your channel URL]"</div>
      </div>
    </div>
    <div class="section">
      <div class="section-title">SEO Best Practices</div>
      <ul class="command-list">
        <li class="command-item"><span class="command-label">Keyword in Title</span><span class="command-desc">Place your primary keyword in the first 5 words of the title</span></li>
        <li class="command-item"><span class="command-label">Tags</span><span class="command-desc">Use 5–10 tags per video: mix broad and specific terms</span></li>
        <li class="command-item"><span class="command-label">Description Length</span><span class="command-desc">Aim for 150–300 words: enough for context, not overwhelming</span></li>
        <li class="command-item"><span class="command-label">Channel Description</span><span class="command-desc">Write a 2–3 sentence channel bio with your top 3 keywords</span></li>
        <li class="command-item"><span class="command-label">Custom URL</span><span class="command-desc">Set a clean, keyword-rich URL for your channel (e.g., open.video/c/yourname)</span></li>
      </ul>
    </div>
  </div>

  <!-- TAB: ENGAGEMENT -->
  <div class="tab-panel" id="tab-engagement">
    <div class="section">
      <div class="section-title">Engagement Best Practices</div>
      <div class="practice-card open" id="eng-card1">
        <div class="practice-header" onclick="toggleCard('eng-card1')">
          <span class="practice-icon">🎯</span>
          <span class="practice-title">CTAs That Convert</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>End every video with one clear CTA: subscribe, download, or book a call</li>
            <li>Add a text overlay CTA in the last 10 seconds of the video</li>
            <li>Include the CTA link in the first line of your description</li>
          </ul>
        </div>
      </div>
      <div class="practice-card" id="eng-card2">
        <div class="practice-header" onclick="toggleCard('eng-card2')">
          <span class="practice-icon">📺</span>
          <span class="practice-title">Binge-Watching by Design</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>Use playlists to guide viewers from one video into the next</li>
            <li>Create a "Getting Started" or "Start Here" playlist as your entry point</li>
            <li>Feature one "hero video" that introduces your brand and channel</li>
          </ul>
        </div>
      </div>
      <div class="practice-card" id="eng-card3">
        <div class="practice-header" onclick="toggleCard('eng-card3')">
          <span class="practice-icon">📧</span>
          <span class="practice-title">Lead Capture</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>Integrate email opt-in with Mailchimp, ConvertKit, or Substack</li>
            <li>Create a "thank you" automation for every new lead</li>
            <li>Every viewer who leaves without an email is a missed opportunity</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- TAB: DISTRIBUTION -->
  <div class="tab-panel" id="tab-distribution">
    <div class="section">
      <div class="section-title">Distribution Best Practices</div>
      <div class="practice-card open" id="dist-card1">
        <div class="practice-header" onclick="toggleCard('dist-card1')">
          <span class="practice-icon">🔗</span>
          <span class="practice-title">Where to Share</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>Share your channel link weekly on LinkedIn and Substack</li>
            <li>Use short teasers (30s–1min) on TikTok, Reels, or YouTube</li>
            <li>Always drive traffic back to <em>your domain</em>, not external platforms</li>
            <li>Publish a Substack post embedding 1 video per week</li>
          </ul>
        </div>
      </div>
      <div class="practice-card" id="dist-card2">
        <div class="practice-header" onclick="toggleCard('dist-card2')">
          <span class="practice-icon">🎬</span>
          <span class="practice-title">Teaser Strategy</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>Create a 30–60 second clip from your best videos for TikTok and Reels</li>
            <li>End every teaser with a direct link back to the full video on your channel</li>
            <li>YouTube teasers work well too: use it as a discovery layer, not a destination</li>
          </ul>
        </div>
      </div>
    </div>
  </div>

  <!-- TAB: GROWTH -->
  <div class="tab-panel" id="tab-growth">
    <div class="section">
      <div class="section-title">Growth Best Practices</div>
      <div class="practice-card open" id="growth-card1">
        <div class="practice-header" onclick="toggleCard('growth-card1')">
          <span class="practice-icon">📈</span>
          <span class="practice-title">Consistency &amp; Analytics</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>Upload consistently: 1 to 2 videos per week is the target</li>
            <li>Check analytics weekly to see which videos are ranking in Google</li>
            <li>Double down on high-performing topics: make more of what's working</li>
            <li>Review and refresh your featured video monthly</li>
          </ul>
        </div>
      </div>
      <div class="practice-card" id="growth-card2">
        <div class="practice-header" onclick="toggleCard('growth-card2')">
          <span class="practice-icon">🔄</span>
          <span class="practice-title">The Content Flywheel</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>One longform video is the source; everything else is repurposed from it</li>
            <li>Clip 3–5 short segments for social</li>
            <li>Embed the full video in one Substack post</li>
            <li>Create 2–3 social teasers pointing back to the channel</li>
          </ul>
        </div>
      </div>
    </div>
    <div class="flywheel">
      <div class="label">The Content Flywheel</div>
      <div class="flywheel-steps">
        <div class="flywheel-step">1 Longform Video</div>
        <div class="flywheel-arrow">→</div>
        <div class="flywheel-step">3–5 Clips</div>
        <div class="flywheel-arrow">→</div>
        <div class="flywheel-step">1 Substack Embed</div>
        <div class="flywheel-arrow">→</div>
        <div class="flywheel-step">2–3 Social Teasers</div>
      </div>
    </div>
  </div>

  <!-- TAB: YOUTUBE IMPORT -->
  <div class="tab-panel" id="tab-youtube">
    <div class="section">
      <div class="section-title">How to Connect Your YouTube Channel</div>
      <ul class="command-list">
        <li class="command-item"><span class="command-label">Step 1</span><span class="command-desc">In your Open.Video dashboard, go to Settings → Integrations → YouTube</span></li>
        <li class="command-item"><span class="command-label">Step 2</span><span class="command-desc">Click "Connect YouTube Channel" → authorize with your Google account</span></li>
        <li class="command-item"><span class="command-label">Step 3</span><span class="command-desc">Select which YouTube channel to link (if you manage more than one)</span></li>
        <li class="command-item"><span class="command-label">Step 4</span><span class="command-desc">Your YouTube library appears: select individual videos or import in bulk</span></li>
        <li class="command-item"><span class="command-label">Step 5</span><span class="command-desc">Choose your destination channel on Open.Video and confirm import</span></li>
      </ul>
    </div>
    <div class="section">
      <div class="section-title">What Gets Imported</div>
      <ul class="command-list">
        <li class="command-item"><span class="command-label">Video file</span><span class="command-desc">The full video is pulled directly from YouTube: no re-uploading needed</span></li>
        <li class="command-item"><span class="command-label">Title</span><span class="command-desc">Imported as-is; you can edit it after import to optimize for Open.Video SEO</span></li>
        <li class="command-item"><span class="command-label">Description</span><span class="command-desc">Carried over: review and update with your Open.Video description formula</span></li>
        <li class="command-item"><span class="command-label">Thumbnail</span><span class="command-desc">Imported with the video: can be replaced after import</span></li>
      </ul>
    </div>
    <div class="section">
      <div class="section-title">What to Do After Importing</div>
      <div class="practice-card open" id="yt-card1">
        <div class="practice-header" onclick="toggleCard('yt-card1')">
          <span class="practice-icon">✏️</span>
          <span class="practice-title">Optimize Every Imported Video</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>Rewrite the title using the Open.Video formula: [Keyword] + [Benefit]</li>
            <li>Update the description: add your channel link and a CTA</li>
            <li>Add up to 10 keyword tags specific to your niche</li>
            <li>Assign each video to a playlist: don't leave them floating</li>
          </ul>
        </div>
      </div>
      <div class="practice-card" id="yt-card2">
        <div class="practice-header" onclick="toggleCard('yt-card2')">
          <span class="practice-icon">📂</span>
          <span class="practice-title">Organize Before You Publish</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>Don't import everything at once: start with your 5–10 best performing videos</li>
            <li>Group imports by topic so you can build playlists immediately</li>
            <li>Set one imported video as your channel's featured "hero" video</li>
            <li>Keep private any videos that need editing before they go live</li>
          </ul>
        </div>
      </div>
      <div class="practice-card" id="yt-card3">
        <div class="practice-header" onclick="toggleCard('yt-card3')">
          <span class="practice-icon">🔗</span>
          <span class="practice-title">YouTube vs. Open.Video: How to Use Both</span>
          <span class="practice-toggle">+</span>
        </div>
        <div class="practice-body">
          <ul>
            <li>Keep posting on YouTube: it's a discovery engine, not your home base</li>
            <li>Use YouTube as a top-of-funnel teaser; Open.Video as the owned destination</li>
            <li>Always include your Open.Video channel link in every YouTube description</li>
            <li>Drive YouTube subscribers to your channel with a pinned comment or end screen</li>
          </ul>
        </div>
      </div>
    </div>
    <div class="formula-box">
      <div class="label">The YouTube → Open.Video Workflow</div>
      <p>Post on YouTube for discovery → link to Open.Video in every description → own the audience that clicks through</p>
      <div class="example">You get the algorithm reach of YouTube AND the ownership of your own branded channel. That's the play.</div>
    </div>
  </div>

</div>
<script>
  function switchTab(name, btn) {
    document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
    document.querySelectorAll('.tab-panel').forEach(p => p.classList.remove('active'));
    document.getElementById('tab-' + name).classList.add('active');
    btn.classList.add('active');
  }
  function toggleCard(id) {
    document.getElementById(id).classList.toggle('open');
  }
</script>
</body>
</html>`;

export default function OpenVideoReferenceCard() {
  return (
    <GuideShell
      context={"Open.Video guide"}
      backHref={"/resources/open-video"}
      backLabel={"Back to Open.Video"}
    >
      <GuideFrame html={HTML_CONTENT} title={"Open.Video Commands & Best Practices"} />
    </GuideShell>
  );
}
